import Axios, { AxiosInstance, AxiosResponse, AxiosRequestConfig as IAxiosRequestConfig, Method } from 'axios';
import { AppErrorStatusCode } from 'common/constants';
import { sleep } from 'common/utils';
import { retrieveUserAuthToken } from './db-service';
import { config } from 'config';
import { AppStateController } from 'common/controller/AppStateController';
import ResponseUtil from 'common/utils/response-util';

type AxiosRequestConfig = IAxiosRequestConfig<any> & {
  retry?: number;
  retryDelay?: number;
};

const defaultConfig = {
  returnRejectedPromiseOnError: true,
  timeout: 20000,
  baseURL: config.api.rest,
  withCredentials: true,
  'Access-Control-Expose-Headers': 'Set-Cookie',
} as AxiosRequestConfig;

const httpClient: AxiosInstance = Axios.create(defaultConfig);

const convertHttpMethod = (method?: Method) => {
  switch (method) {
    case 'get':
      return 'GET';
    case 'delete':
      return 'DELETE';
    case 'post':
      return 'POST';
    case 'put':
      return 'PUT';
    case 'head':
      return 'HEAD';
    case 'options':
      return 'OPTIONS';
    case 'patch':
      return 'PATCH';
    case 'purge':
      return 'PURGE';
    case 'link':
      return 'LINK';
    case 'unlink':
      return 'UNLINK';
    default:
      console.log('METHOD NOT MATCHED>>');
      return '';
  }
};

httpClient.interceptors.response.use(
  async function (response) {
    AppStateController.getInstance().updateAppErrorListeners(AppErrorStatusCode.NONE);
    return response;
  },
  async function (error) {
    try {
      // Request failed, e.g. HTTP code 500
      console.log('INTERCEPTOR_ERROR_RESP: ', error.code, ':::', error.response?.status, JSON.stringify(error));
      if (error.response?.status) {
        AppStateController.getInstance().updateAppErrorListeners(error.response?.status || error.code);
      } else if (error.code) {
        AppStateController.getInstance().updateAppErrorListeners(error.code);
      }
    } finally {
      return Promise.reject(error);
    }
  },
);

httpClient.defaults.headers.common = {
  'Cache-Control': 'no-cache, no-store, must-revalidate',
  Pragma: 'no-cache',
  'Content-Type': 'application/json',
  Accept: 'application/json',
  'x-version-name': config.version,
};

export const initHtttpAuthHeader = (brandId?: number) => {
  const tkn = retrieveUserAuthToken();
  console.log('httpClient: updateAuthToken>>', tkn);
  httpClient.defaults.headers.common['Authorization'] = 'Bearer ' + tkn;
  httpClient.defaults.headers.common['x-brand-id'] = brandId ? brandId.toString() : '';
};

initHtttpAuthHeader();

export const updateUserDataInHttpHeader = async (brandId: number) => {
  console.log('httpClient: Updating brandId in header:', brandId);
  httpClient.defaults.headers.common['x-brand-id'] = brandId.toString();
};

const httpRequest = (axiosConfig: AxiosRequestConfig, retryCount = 0): Promise<any> => {
  const timeoutDuration = axiosConfig.timeout || defaultConfig.timeout!;
  console.log('timeoutDuration', timeoutDuration);
  const { intervalId, signal } = ResponseUtil.getTimeoutConfig(timeoutDuration);
  axiosConfig.signal = signal;
  return httpClient(axiosConfig)
    .then((response: any) => {
      return Promise.resolve(response);
    })
    .catch(async (error) => {
      if (!axiosConfig.retry || !axiosConfig.retryDelay || retryCount > axiosConfig.retry) {
        return Promise.reject(error);
      }
      const message = error.message;
      // retry while Network timeout or Network Error
      if (message !== 'canceled' && axiosConfig.method !== 'GET' && axiosConfig.method !== 'PUT') {
        return Promise.reject(error);
      }
      console.log('retryingAPI', axiosConfig.url);
      await sleep(axiosConfig.retryDelay);
      return httpRequest(axiosConfig, retryCount + 1);
    })
    .finally(() => {
      clearTimeout(intervalId);
    });
};

type GetRequest = <T = any, R = AxiosResponse<T>, D = any>(url: string, config?: AxiosRequestConfig) => Promise<R>;
type PostRequest = <T = any, R = AxiosResponse<T>, D = any>(
  url: string,
  data?: D,
  config?: AxiosRequestConfig,
) => Promise<R>;
type PutRequest = <T = any, R = AxiosResponse<T>, D = any>(
  url: string,
  data?: D,
  config?: AxiosRequestConfig,
) => Promise<R>;

httpClient.get = ((url, axiosConfig) => {
  return httpRequest({ url, ...axiosConfig, method: 'GET' });
}) as GetRequest;

httpClient.put = ((url, data, axiosConfig) => {
  return httpRequest({ url, ...axiosConfig, data, method: 'PUT' });
}) as PutRequest;

httpClient.post = ((url, data, axiosConfig) => {
  return httpRequest({ url, ...axiosConfig, data, method: 'POST' });
}) as PostRequest;

export default httpClient;
